import React, { useState } from 'react';

import Button from 'components/Button/Button';
import { cloneDeep } from 'lodash';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { PublishMenuProps } from '../../../types';
import modalCommonStyles from './ModalCommon.module.scss';
import { listAppsInString, useErrorModal } from '../../../utils';

interface ComponentProps {
  styles: {
    [key: string]: string;
  };
  closeModal: () => void;
}

const PublishMenu: React.FC<PublishMenuProps & ComponentProps> = ({
  affectedItemsAndOptionsApps,
  affectedApps,
  callback,
  styles,
  closeModal,
  firstPublish,
  hasBrands,
  venueName,
  menu
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const errorModalHandler = useErrorModal();
  const clonedAffectedApps = cloneDeep(affectedApps);
  const clonedAffectedItemsAndOptionsApps = cloneDeep(affectedItemsAndOptionsApps);
  const itemsAndOptionsAppsFiltered = clonedAffectedItemsAndOptionsApps.filter(
    (app) => !clonedAffectedApps.some((c) => c.id === app.id)
  );
  const appToLog =
    menu.apps[0].id.includes('virtual') && menu.apps[0].brand ? `${menu.apps[0].name} - brand` : menu.apps[0].name;
  const body: JSX.Element = firstPublish ? (
    <span>
      Your menu will be published to {listAppsInString(clonedAffectedApps, hasBrands, venueName)} and made live for your
      customers to order from. <br />
      <br />
      If you&apos;re not ready to go live, head to the top of your menu to save.
    </span>
  ) : (
    <span>
      Your menu will be published to {listAppsInString(clonedAffectedApps, hasBrands, venueName)}
      {itemsAndOptionsAppsFiltered.length ? (
        <>
          <br /> <br /> Edits to items you have across{' '}
          {listAppsInString(itemsAndOptionsAppsFiltered, hasBrands, venueName)} will also be published
        </>
      ) : (
        ''
      )}
      .
    </span>
  );
  return (
    <div data-testid="menu-publish-confirm">
      <h2 className={styles.NoMargin}>Publish?</h2>
      <p data-testid="menu-publish-confirm-body">{body}</p>
      <div className={`${styles.ButtonRow}`}>
        <Button
          onClick={() => {
            logAmplitudeEvent({
              name: 'publish menu (cancel)',
              category: AmplitudeEventCategories.MM_MENU,
              properties: {
                [AmplitudeEventProperties.MENU_ID]: menu.mmId,
                [AmplitudeEventProperties.MENU_NAME]: menu.name,
                [AmplitudeEventProperties.APP]: appToLog
              }
            });
            closeModal();
          }}
          level="low"
        >
          <span>Cancel</span>
        </Button>
        <Button
          disabled={isLoading}
          onClick={async () => {
            setIsLoading(true);
            const error = await callback();
            setIsLoading(false);
            if (error) {
              errorModalHandler(error.error, error.currentVenue);
            } else {
              closeModal();
            }
            logAmplitudeEvent({
              name: 'publish menu (confirm)',
              category: AmplitudeEventCategories.MM_MENU,
              properties: {
                [AmplitudeEventProperties.MENU_ID]: menu.mmId,
                [AmplitudeEventProperties.MENU_NAME]: menu.name,
                [AmplitudeEventProperties.RESULT]: error ? 'failure' : 'success',
                [AmplitudeEventProperties.APP]: appToLog
              }
            });
          }}
        >
          <span className={modalCommonStyles.ConfirmButton}>
            {isLoading ? <i className="mm-circle-o-notch mm-spin" /> : 'Confirm'}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default PublishMenu;
